import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Shield, Database, Eye, Lock, UserCheck, Globe, Trash2, Mail } from 'lucide-react';

const sections = [
  {
    id: 'engagement',
    icon: Shield,
    title: "Engagement de protection",
    content: [
      "Chez 10 Millions Investor, la protection de vos données personnelles est une priorité absolue. Cette politique de confidentialité décrit comment nous collectons, utilisons, stockons et protégeons vos informations lorsque vous utilisez notre plateforme.",
      "Nous nous engageons à respecter le Règlement Général sur la Protection des Données (RGPD) et la loi Informatique et Libertés dans le traitement de vos données personnelles."
    ]
  },
  {
    id: 'collecte',
    icon: Database,
    title: "Données collectées",
    content: [
      "Nous collectons uniquement les données nécessaires au fonctionnement de la plateforme :"
    ],
    list: [
      { label: "Données d'identification", desc: "Nom, prénom, adresse email, numéro de téléphone" },
      { label: "Données de connexion", desc: "Adresse IP, logs de connexion, type de navigateur" },
      { label: "Données de paiement", desc: "Traitées de manière sécurisée par notre prestataire Square. Nous ne stockons jamais vos données bancaires" },
      { label: "Données d'utilisation", desc: "Simulations effectuées, articles consultés, interactions avec la communauté" },
      { label: "Documents uploadés", desc: "Fichiers PDF soumis à l'assistant IA (compromis, baux). Ces documents sont analysés de manière sécurisée et ne sont pas partagés" }
    ]
  },
  {
    id: 'finalites',
    icon: Eye,
    title: "Finalités du traitement",
    content: ["Vos données sont utilisées exclusivement pour :"],
    list: [
      { label: "Gestion de votre compte", desc: "Création, authentification et gestion de votre abonnement" },
      { label: "Fourniture des services", desc: "Calculs de rentabilité, assistant IA, blog communautaire, annuaire" },
      { label: "Communication", desc: "Emails liés à votre compte, alertes immobilières (si activées), newsletter (avec votre consentement)" },
      { label: "Amélioration de la plateforme", desc: "Analyse anonymisée de l'utilisation pour améliorer nos outils et services" }
    ]
  },
  {
    id: 'securite',
    icon: Lock,
    title: "Sécurité des données",
    content: [
      "Nous mettons en œuvre des mesures techniques et organisationnelles robustes pour protéger vos données :"
    ],
    list: [
      { label: "Chiffrement", desc: "Toutes les communications sont chiffrées via HTTPS/TLS" },
      { label: "Authentification sécurisée", desc: "Mots de passe hashés avec des algorithmes de dernière génération" },
      { label: "Hébergement", desc: "Données hébergées sur des serveurs sécurisés conformes aux normes européennes" },
      { label: "Accès restreint", desc: "Seul le personnel habilité a accès aux données personnelles" }
    ]
  },
  {
    id: 'partage',
    icon: Globe,
    title: "Partage et transfert des données",
    content: [
      "Nous ne vendons jamais vos données personnelles. Vos données peuvent être partagées avec :"
    ],
    list: [
      { label: "Square", desc: "Pour le traitement sécurisé des paiements (web uniquement)" },
      { label: "Apple (RevenueCat)", desc: "Pour le traitement sécurisé des paiements in-app sur iOS" },
      { label: "Resend", desc: "Pour l'envoi d'emails transactionnels" },
      { label: "OpenAI & Anthropic", desc: "Fournisseurs d'IA tiers utilisés par nos assistants IA. Seul le texte de vos requêtes immobilières (questions, recherches, analyses) leur est transmis — jamais vos identifiants, mot de passe ou données bancaires. Aucune donnée vocale n'est envoyée : la reconnaissance vocale s'effectue localement sur votre appareil par iOS, puis seul le texte transcrit est transmis. Un consentement explicite vous est demandé avant la première utilisation de toute fonctionnalité IA." }
    ],
    extra: "Aucun transfert de données en dehors de l'Union Européenne n'est effectué sans garanties appropriées (clauses contractuelles types, certifications)."
  },
  {
    id: 'droits',
    icon: UserCheck,
    title: "Vos droits",
    content: [
      "Conformément au RGPD, vous disposez des droits suivants :"
    ],
    list: [
      { label: "Droit d'accès", desc: "Obtenir une copie de toutes les données que nous détenons sur vous" },
      { label: "Droit de rectification", desc: "Corriger toute donnée inexacte ou incomplète" },
      { label: "Droit à l'effacement", desc: "Demander la suppression de vos données personnelles" },
      { label: "Droit à la portabilité", desc: "Recevoir vos données dans un format structuré et lisible" },
      { label: "Droit d'opposition", desc: "Vous opposer au traitement de vos données à des fins de prospection" },
      { label: "Droit à la limitation", desc: "Demander la limitation du traitement de vos données" }
    ],
    extra: "Pour exercer l'un de ces droits, contactez-nous à bonjour@10millionsinvestor.com. Nous nous engageons à répondre dans un délai de 30 jours."
  },
  {
    id: 'conservation',
    icon: Trash2,
    title: "Conservation des données",
    content: [
      "Vos données personnelles sont conservées pendant toute la durée de votre abonnement et jusqu'à 3 ans après la résiliation de votre compte, conformément aux obligations légales.",
      "Les données de paiement sont conservées conformément aux obligations comptables et fiscales (10 ans).",
      "Les logs de connexion sont conservés pendant 1 an conformément à la réglementation en vigueur.",
      "À l'issue de ces durées, vos données sont définitivement supprimées de nos systèmes."
    ]
  },
  {
    id: 'cookies',
    icon: Mail,
    title: "Cookies",
    content: [
      "Notre plateforme utilise des cookies techniques strictement nécessaires au fonctionnement du service (authentification, préférences). Nous n'utilisons pas de cookies publicitaires ou de traçage à des fins commerciales.",
      "Vous pouvez configurer votre navigateur pour refuser les cookies, mais certaines fonctionnalités de la plateforme pourraient ne plus être disponibles."
    ]
  }
];

const trustBadges = [
  { label: "Conforme RGPD", color: "bg-[#0D47A1]" },
  { label: "Données chiffrées", color: "bg-[#00B050]" },
  { label: "Aucune revente", color: "bg-slate-700" },
  { label: "Hébergement UE", color: "bg-[#0D47A1]" },
];

const ConfidentialitePage = () => {
  const [activeSection, setActiveSection] = useState('engagement');

  useEffect(() => {
    const handleScroll = () => {
      for (const s of sections) {
        const el = document.getElementById(s.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 140 && rect.bottom > 140) {
            setActiveSection(s.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="privacy-page">
      {/* Nav */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_4ed8f34b-1bc9-42f5-8577-4843189c4948/artifacts/jd7g0w5b_LOGO%2010MILLIONSINVESTOR%20blanc.png"
              alt="10M Investor" className="h-7"
            />
          </Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1 transition-colors" data-testid="privacy-back-link">
            <ChevronLeft className="w-4 h-4" />
            Retour
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(0,176,80,0.25)_0%,_transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(13,71,161,0.2)_0%,_transparent_60%)]" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 relative">
          <div className="flex items-center gap-2 mb-4">
            <Link to="/" className="text-slate-500 text-sm hover:text-slate-300 transition-colors">Accueil</Link>
            <ChevronRight className="w-3 h-3 text-slate-600" />
            <span className="text-[#00B050] text-sm font-medium">Confidentialité</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight mb-4">
            Politique de<br />
            <span className="bg-gradient-to-r from-[#00B050] to-[#0D47A1] bg-clip-text text-transparent">Confidentialité</span>
          </h1>
          <p className="text-slate-400 max-w-xl mb-8">
            Nous prenons la protection de vos données au sérieux. Découvrez comment nous les collectons, les utilisons et les protégeons.
          </p>
          {/* Trust Badges */}
          <div className="flex flex-wrap gap-2">
            {trustBadges.map((b, i) => (
              <span key={i} className={`${b.color} text-white text-xs font-semibold px-3 py-1.5 rounded-full`}>
                {b.label}
              </span>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-6">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <div className="w-2 h-2 rounded-full bg-[#00B050] animate-pulse" />
              Dernière mise à jour : Mars 2026
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-[220px_1fr] gap-10">
          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-20">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-4">Sommaire</p>
              <nav className="space-y-1">
                {sections.map((s, idx) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    onClick={(e) => { e.preventDefault(); document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                      activeSection === s.id
                        ? 'bg-[#00B050]/10 text-[#00B050] font-semibold'
                        : 'text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                    }`}
                    data-testid={`privacy-toc-${s.id}`}
                  >
                    <span className={`w-5 h-5 rounded-md text-[10px] font-bold flex items-center justify-center flex-shrink-0 ${
                      activeSection === s.id ? 'bg-[#00B050] text-white' : 'bg-slate-200 text-slate-500'
                    }`}>{idx + 1}</span>
                    <span className="truncate">{s.title}</span>
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Sections */}
          <div className="space-y-6">
            {sections.map((section, idx) => (
              <section
                key={section.id}
                id={section.id}
                className="bg-white rounded-2xl border border-slate-100 p-6 sm:p-8 hover:shadow-lg hover:shadow-slate-100/50 transition-all duration-300 scroll-mt-20"
                data-testid={`privacy-section-${section.id}`}
              >
                <div className="flex items-start gap-4 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-[#00B050]/5 flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-5 h-5 text-[#00B050]" />
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#00B050]/60 mb-1">Article {idx + 1}</p>
                    <h2 className="text-lg font-bold text-slate-900">{section.title}</h2>
                  </div>
                </div>
                <div className="pl-14 space-y-3">
                  {section.content.map((p, i) => (
                    <p key={i} className="text-slate-600 leading-relaxed text-[15px]">{p}</p>
                  ))}
                  {section.list && (
                    <div className="space-y-3 mt-4">
                      {section.list.map((item, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-[#00B050] mt-2.5 flex-shrink-0" />
                          <div className="text-[15px]">
                            <span className="font-semibold text-slate-800">{item.label}</span>
                            <span className="text-slate-600"> — {item.desc}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {section.extra && (
                    <p className="text-slate-600 leading-relaxed mt-4 text-[15px]">{section.extra}</p>
                  )}
                </div>
              </section>
            ))}

            {/* CTA */}
            <div className="bg-slate-900 rounded-2xl p-8 sm:p-10 relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(0,176,80,0.15)_0%,_transparent_70%)]" />
              <div className="relative text-center">
                <Shield className="w-10 h-10 text-[#00B050] mx-auto mb-4" />
                <p className="text-white font-bold text-lg mb-1">Délégué à la Protection des Données</p>
                <a href="mailto:bonjour@10millionsinvestor.com" className="text-[#00B050] font-bold text-xl hover:text-white transition-colors" data-testid="privacy-contact-email">
                  bonjour@10millionsinvestor.com
                </a>
                <p className="text-slate-500 text-sm mt-4">
                  Vous pouvez également introduire une réclamation auprès de la CNIL : <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-300">www.cnil.fr</a>
                </p>
                <div className="flex items-center justify-center gap-6 mt-6">
                  <Link to="/cgv" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1">
                    CGV <ChevronRight className="w-3 h-3" />
                  </Link>
                  <Link to="/contact" className="text-sm text-slate-400 hover:text-white transition-colors flex items-center gap-1">
                    Contact <ChevronRight className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-6 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-2">
          <span>10 Millions Investor &copy; 2026</span>
          <div className="flex items-center gap-4">
            <Link to="/cgv" className="hover:text-slate-600 transition-colors">CGV</Link>
            <Link to="/contact" className="hover:text-slate-600 transition-colors">Contact</Link>
            <Link to="/charte" className="hover:text-slate-600 transition-colors">Charte</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ConfidentialitePage;
